import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// Pulse animation
const pulse = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(63, 81, 181, 0.7);
  }
  
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(63, 81, 181, 0);
  }
  
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(63, 81, 181, 0);
  }
`;

const StatusContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const StatusDot = styled(Box)(({ theme, status }) => {
  // Status color mapping
  const colorMap = {
    connected: theme.palette.success.main,
    connecting: theme.palette.warning.main,
    disconnected: theme.palette.error.main,
    error: theme.palette.error.dark,
    streaming: theme.palette.primary.main,
  };

  return {
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: colorMap[status] || theme.palette.grey[500],
    animation: status === 'streaming' ? `${pulse} 2s infinite` : 'none',
  };
});

const StatusText = styled(Typography)(({ theme, status }) => {
  // Status color mapping
  const colorMap = {
    connected: theme.palette.success.main,
    connecting: theme.palette.warning.main,
    disconnected: theme.palette.error.main,
    error: theme.palette.error.dark,
    streaming: theme.palette.primary.main,
  };

  return {
    color: colorMap[status] || theme.palette.text.primary,
    fontWeight: 500,
  };
});

/**
 * Status indicator component
 * @param {Object} props Component props
 * @param {string} props.status Current status (connected, connecting, disconnected, error, streaming)
 * @param {string} props.label Optional label to display
 * @param {boolean} props.showTooltip Whether to show tooltip on hover (default: true)
 * @returns {JSX.Element} StatusIndicator component
 */
const StatusIndicator = ({ status, label, showTooltip = true }) => {
  // Status descriptions for tooltips
  const statusDescriptions = {
    connected: 'Successfully connected to the server',
    connecting: 'Attempting to establish connection',
    disconnected: 'Not connected to the server',
    error: 'Connection error occurred',
    streaming: 'Actively streaming audio',
  };

  // Format status for display
  const formatStatus = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const statusContent = (
    <StatusContainer>
      <StatusDot status={status} />
      <StatusText variant="body2" status={status}>
        {label || formatStatus(status)}
      </StatusText>
    </StatusContainer>
  );

  // If tooltip is disabled, return content without tooltip
  if (!showTooltip) {
    return statusContent;
  }

  // Otherwise, wrap content in tooltip
  return (
    <Tooltip title={statusDescriptions[status] || ''} arrow>
      {statusContent}
    </Tooltip>
  );
};

export default StatusIndicator;
